import { useDesktop } from "#hooks/use-desktop"
import { useI18n } from "#hooks/use-i18n"
import type { DashboardFilters as DashboardFiltersType } from "@home-finance-manager/shared/types/dashboard"
import { Filter, LayoutGrid, RefreshCw, Sparkles } from "lucide-react"
import { DateRangePicker } from "../DateRangePicker"
import { Button } from "../ui/button"
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "../ui/drawer"
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover"
import DashboardFilters from "./DashboardFilters"

interface DashboardHeaderActionsProps {
  isLoading: boolean
  onRefetch: () => void
  filters: DashboardFiltersType
  onFiltersChange: (filters: DashboardFiltersType) => void
  isFiltersOpen: boolean
  onFiltersOpenChange: (open: boolean) => void
}

export function DashboardHeaderActions({
  isLoading,
  onRefetch,
  filters,
  onFiltersChange,
  isFiltersOpen,
  onFiltersOpenChange,
}: DashboardHeaderActionsProps) {
  const { t } = useI18n()
  const isDesktop = useDesktop()
  const includeVirtualExpenses = filters.includeVirtualExpenses === true

  const handleToggleVirtualExpenses = () => {
    onFiltersChange({
      ...filters,
      includeVirtualExpenses: !includeVirtualExpenses ? true : undefined,
    })
  }

  const handleDateRangeChange = (startDate: string | undefined, endDate: string | undefined) => {
    onFiltersChange({
      ...filters,
      startDate,
      endDate,
    })
  }

  return (
    <div className="flex gap-1 sm:gap-2">
      <DateRangePicker
        startDate={filters.startDate}
        endDate={filters.endDate}
        onDateRangeChange={handleDateRangeChange}
      />
      <div className="flex items-center gap-1 sm:gap-2">
        <Button
          variant={includeVirtualExpenses ? "default" : "outline"}
          size="icon"
          onClick={handleToggleVirtualExpenses}
          title={
            includeVirtualExpenses
              ? t("components.dashboard.headerActions.hideVirtual")
              : t("components.dashboard.headerActions.includeVirtual")
          }
          className="shadow-sm dark:shadow-none dark:border-border/50 hover:shadow-md dark:hover:shadow-none transition-shadow"
          data-tourid="virtual-expenses-toggle"
        >
          {includeVirtualExpenses ? (
            <Sparkles className="w-5 h-5" />
          ) : (
            <LayoutGrid className="w-5 h-5" />
          )}
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={onRefetch}
          disabled={isLoading}
          title={t("components.dashboard.headerActions.refresh")}
          className="shadow-sm dark:shadow-none dark:border-border/50 hover:shadow-md dark:hover:shadow-none transition-shadow"
          data-tourid="refresh-button"
        >
          <RefreshCw className={`w-5 h-5 ${isLoading ? "animate-spin" : ""}`} />
        </Button>
        {isDesktop ? (
          <Popover open={isFiltersOpen} onOpenChange={onFiltersOpenChange}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="relative shadow-sm dark:shadow-none dark:border-border/50 hover:shadow-md dark:hover:shadow-none transition-shadow"
                data-tourid="filters-button"
              >
                <Filter className="w-5 h-5" />
                {(filters.categoryIds && filters.categoryIds.length > 0) ||
                (filters.partnerIds && filters.partnerIds.length > 0) ||
                filters.search ? (
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full ring-2 ring-background" />
                ) : null}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="end">
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium leading-none mb-4">
                    {t("components.dashboard.filters.title")}
                  </h4>
                  <DashboardFilters
                    filters={filters}
                    onFiltersChange={onFiltersChange}
                    onApply={() => {
                      onFiltersOpenChange(false)
                    }}
                    onReset={() => {
                      onFiltersOpenChange(false)
                    }}
                  />
                </div>
              </div>
            </PopoverContent>
          </Popover>
        ) : (
          <Drawer open={isFiltersOpen} onOpenChange={onFiltersOpenChange}>
            <DrawerTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="relative shadow-sm dark:shadow-none dark:border-border/50 hover:shadow-md dark:hover:shadow-none transition-shadow"
                data-tourid="filters-button"
              >
                <Filter className="w-5 h-5" />
                {(filters.categoryIds && filters.categoryIds.length > 0) ||
                (filters.partnerIds && filters.partnerIds.length > 0) ||
                filters.search ? (
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full ring-2 ring-background" />
                ) : null}
              </Button>
            </DrawerTrigger>
            <DrawerContent>
              <DrawerHeader>
                <DrawerTitle>{t("components.dashboard.filters.title")}</DrawerTitle>
                <DrawerDescription>
                  {t("components.dashboard.filters.description")}
                </DrawerDescription>
              </DrawerHeader>
              <div className="px-4 pb-4 overflow-y-auto">
                <DashboardFilters
                  filters={filters}
                  onFiltersChange={onFiltersChange}
                  onApply={() => {
                    onFiltersOpenChange(false)
                  }}
                  onReset={() => {
                    onFiltersOpenChange(false)
                  }}
                />
              </div>
            </DrawerContent>
          </Drawer>
        )}
      </div>
    </div>
  )
}
